<script lang="ts">
	import { onMount, onDestroy, tick } from 'svelte';
	import { get } from 'svelte/store';

	import { format } from 'svelte-i18n';

	import { Text, Alert, Loader, Button, Icon } from '@Shelly/shelly-design-system';
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { sendAgentMessage } from '../../services/agent-api';
	import type { ChatMessage as ChatMessageType } from '../../services/ai-scene-chat-api';
	import { postReq } from '../../services/http';

	import { user_data } from 'src/stores/account/user';
	import { ai_prompt_count } from 'src/stores/features/ai-model';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { rooms } from 'src/stores/lists/rooms';
	import { scenes } from 'src/stores/lists/scenes';
	import { MAX_AI_MODEL_LIMIT, AI_PROMPT_LIMIT_PREMIUM } from '../../constants';
	import { push } from 'svelte-spa-router';
	import { mobile } from '../../services/mobile-rework';
	import { isMotion } from '../../helpers/interface_helpers';
	import ChatMessage from './ChatMessage.svelte';
	import ChatEmptyState from './ChatEmptyState.svelte';
	import RichMentionInput from './RichMentionInput.svelte';
	import ChatSuggestions from './ChatSuggestions.svelte';
	import AgentSelector from './AgentSelector.svelte';
	import PromptUsageBadge from './PromptUsageBadge.svelte';
	import PlusMenuButton from './PlusMenuButton.svelte';
	import TokenStyles from './TokenStyles.svelte';
	import { getAiPromptCount, getSuggestionsForInput, renderTokens } from './helpers';
	import { appendSceneId, getScenesForUpload } from './helpers/multiple-scenes';
	import FlowAnimation from './FlowAnimation.svelte';
	import AIChatInModalPopover from './AIChatInModalPopover.svelte';
	import { checkAgentHealth, setHealthCheckLoadingCallback } from '../../services/agent-health';
	import { getAgent } from './agents/agents.config';
	export let surfaceOpen = false;

	// Window size tracking
	let windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
	$: isMobileDevice = mobile.isMobile || windowWidth <= 768;

	// Health check loading state
	let isHealthCheckLoading = false;

	// Register callback immediately (before onMount)
	setHealthCheckLoadingCallback((loading) => {
		isHealthCheckLoading = loading;
	});

	function handleResize() {
		windowWidth = window.innerWidth;
	}

	onMount(() => {
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			setHealthCheckLoadingCallback(null);
		};
	});

	// Track modal / dock open state to trigger checks only once per open
	let hasRunInitialChecks = false;
	let healthCheckAlive = true;

	function isChatSurfaceStillOpen() {
		return healthCheckAlive && (get(aiSceneChatStore).isModalOpen || surfaceOpen);
	}

	function resetHealthCheckState() {
		hasRunInitialChecks = false;
		aiSceneChatStore.removeHealthCheckErrors();
	}

	$: isChatSurfaceOpen = $aiSceneChatStore.isModalOpen || surfaceOpen;
	$: if (isChatSurfaceOpen && !hasRunInitialChecks) {
		hasRunInitialChecks = true;
		// Drop leftovers from a previous dock/modal session so they don't flash
		// as an instant "unavailable" message on open.
		aiSceneChatStore.removeHealthCheckErrors();
		getAiPromptCount();
		checkActiveAgentHealth();
	} else if (!isChatSurfaceOpen && hasRunInitialChecks) {
		resetHealthCheckState();
	}

	onDestroy(() => {
		// The dock unmounts ChatWindow without flipping isModalOpen, so the
		// reactive close path above may not run. Always clear on teardown.
		healthCheckAlive = false;
		resetHealthCheckState();
		setHealthCheckLoadingCallback(null);
	});

	// Check health of the active agent
	async function checkActiveAgentHealth(clearPreviousErrors = false) {
		const agentId = $aiSceneChatStore.activeAgentId;
		const agent = getAgent(agentId);

		// Clear previous health check errors if requested (e.g., when switching agents)
		if (clearPreviousErrors) {
			aiSceneChatStore.removeHealthCheckErrors();
		}

		// Use the endpoint for app compatibility even when service health enforcement is disabled.
		if (agent.healthEndpoint) {
			const health = await checkAgentHealth(agentId);
			if (!isChatSurfaceStillOpen()) return;
			aiSceneChatStore.setAgentHealth(agentId, health);

			// Show unavailability message only after all retries failed
			if (health.status === 'unhealthy') {
				const unavailableMessage: ChatMessageType = {
					role: 'assistant',
					content: get(format)('ai_agent_unavailable_message', {
						default:
							"I'm currently unavailable. Please try again later or switch to another assistant.",
					}),
					timestamp: Date.now(),
					isError: true,
					isHealthCheckError: true,
				};
				aiSceneChatStore.addMessage(unavailableMessage);
			}
		} else {
			// Agent doesn't support health checks - clear any previous errors
			// This ensures switching to a non-health-check agent shows suggestions
			if (clearPreviousErrors) {
				aiSceneChatStore.setAgentHealth(agentId, {
					status: 'healthy',
					version: agent.version,
					minimumAppVersion: null,
					isAppVersionSupported: null,
					lastChecked: Date.now(),
					error: null,
				});
			}
		}
	}

	// Retry health check (for health check error messages)
	async function handleHealthCheckRetry() {
		// Remove the health check error message before retrying
		aiSceneChatStore.removeHealthCheckErrors();
		// Re-run the health check
		await checkActiveAgentHealth();
	}

	// Get the appropriate retry handler for a message
	function getRetryHandler(message: ChatMessageType): (() => void) | undefined {
		if (message.isHealthCheckError) return handleHealthCheckRetry;
		if (message.isError) return handleRetry;
		return undefined;
	}

	// Re-check health when agent changes
	let previousAgentId: string | null = null;
	$: {
		const currentAgentId = $aiSceneChatStore.activeAgentId;
		if (previousAgentId !== null && previousAgentId !== currentAgentId) {
			// Clear previous health check errors when switching agents
			checkActiveAgentHealth(true);
		}
		previousAgentId = currentAgentId;
	}

	$: isPremium = $user_data.premium > 0;
	$: promptLimit = isPremium ? AI_PROMPT_LIMIT_PREMIUM : MAX_AI_MODEL_LIMIT;
	$: promptsExhausted = $ai_prompt_count >= promptLimit;

	// Combined loading state for disabling input
	$: isInputDisabled = $aiSceneChatStore.isLoading || isHealthCheckLoading;

	// In-modal onboarding popover state
	$: showOnboardingPopover =
		$aiSceneChatStore.hasAcceptedOnboarding && !$aiSceneChatStore.chatPopoversCompleted;
	$: currentPopoverStep = $aiSceneChatStore.chatPopoverStep;
	$: popoversAllowed = $aiSceneChatStore.chatModalOpenTransitionDone;

	// Anchor refs for popover positioning
	let inputAreaRef: HTMLDivElement;
	let suggestionsAnchorRef: HTMLDivElement;

	// Handle step 1 visibility (suggestions only visible when no messages)
	$: hasMessages = $aiSceneChatStore.messages.length > 0;

	// Auto-advance from step 1 if suggestions aren't visible (user has messages)
	$: if (showOnboardingPopover && currentPopoverStep === 1 && hasMessages) {
		aiSceneChatStore.advanceChatPopoverStep();
	}

	async function handlePopoverNext() {
		aiSceneChatStore.advanceChatPopoverStep();
		await tick();
	}

	function handlePopoverSkip() {
		aiSceneChatStore.skipChatPopovers();
	}

	let userInput = '';
	let messagesContainer: HTMLDivElement;
	let mentionInputComponent: RichMentionInput;
	let isInputFocused = false;
	let mentionActive = false;
	let activeTokenFilter: 'room' | 'device' | 'group' | 'scene' | null = null;
	let mentionFilteredOptions: Array<{
		type: 'room' | 'device' | 'group' | 'scene';
		id: string | number;
		name: string;
		icon: string;
		roomName?: string;
		roomId?: string | number;
	}> = [];

	function handleInputFocus() {
		isInputFocused = true;
	}

	function handleInputBlur() {
		isInputFocused = false;
	}

	function handleMentionChange(e: CustomEvent) {
		mentionActive = e.detail.active;
		mentionFilteredOptions = e.detail.filteredOptions || [];
		if (mentionActive) {
			activeTokenFilter = null;
		}
	}

	function handleTokenTypeSelect(e: CustomEvent<'room' | 'device' | 'group' | 'scene'>) {
		activeTokenFilter = e.detail;
	}

	function handleTokenFilterClear() {
		activeTokenFilter = null;
	}

	function handleSuggestionClick(e: CustomEvent) {
		const { name, type } = e.detail;
		if (mentionActive) {
			const option = mentionFilteredOptions.find((opt) => opt.name === name && opt.type === type);
			if (option) {
				mentionInputComponent?.selectMentionExternal(option);
			}
		} else {
			insertMention(name, type);
		}
		activeTokenFilter = null;
	}

	function handleSuggestionsClose() {
		activeTokenFilter = null;
		mentionActive = false;
		mentionFilteredOptions = [];
	}

	// Expose whether suggestions panel is open (for ESC key handling priority)
	export function areSuggestionsOpen(): boolean {
		return mentionActive || activeTokenFilter !== null;
	}

	// Input history recall (up/down arrow)
	let historyIndex = -1;
	let savedDraft = '';
	$: userMessages = $aiSceneChatStore.messages.filter((m) => m.role === 'user').reverse();
	$: browsingHistory = historyIndex >= 0;

	function handleHistoryUp() {
		const nextIndex = historyIndex + 1;
		if (nextIndex >= userMessages.length) return;
		if (historyIndex === -1) savedDraft = userInput;
		historyIndex = nextIndex;
		mentionInputComponent?.setValue(userMessages[historyIndex].content);
	}

	function handleHistoryDown() {
		if (historyIndex <= -1) return;
		historyIndex -= 1;
		if (historyIndex === -1) {
			mentionInputComponent?.setValue(savedDraft);
			savedDraft = '';
		} else {
			mentionInputComponent?.setValue(userMessages[historyIndex].content);
		}
	}

	// On mobile, show examples when input is not focused and empty
	$: showExamplesOnMobile = !isInputFocused && !userInput.trim();

	// Smart suggestions based on user input - get IDs for highlighting quick chips
	$: suggestedDevices = getSuggestionsForInput(userInput, $devices);
	$: suggestedDeviceIds = new Set(suggestedDevices.map((d) => d.id));

	// Generate dynamic scene suggestions based on user's actual devices
	$: dynamicSceneSuggestions = generateSceneSuggestions($devices, $rooms);

	// Get room, device, group, and scene names for token rendering
	$: roomNames = Object.values($rooms).map((r: any) => r.name as string);
	$: deviceNames = Object.values($devices).map((d: any) => d.name as string);
	$: groupNames = Object.values($groups).map((g: any) => g.name as string);
	$: sceneNames = Object.entries($scenes).map(([id, s]: [string, any]) => s?._meta?.name || id);

	// Render suggestion text with styled tokens (hideGlyphs for cleaner example prompts, showIcons for FA icons)
	function renderSuggestionWithTokens(text: string): string {
		return renderTokens(text, roomNames, deviceNames, {
			classPrefix: 'suggestion-token',
			groupNames,
			sceneNames,
			hideGlyphs: true,
			showIcons: true,
		});
	}

	function generateSceneSuggestions(allDevices: typeof $devices, allRooms: typeof $rooms): string[] {
		const suggestions: string[] = [];
		const deviceList = Object.values(allDevices) as Array<{
			id: string;
			name: string;
			type: string;
		}>;
		const roomList = Object.values(allRooms) as Array<{ id: string; name: string }>;
		const groupList = Object.values($groups) as Array<{ id: string; name: string }>;

		// Find specific device types for relevant suggestions
		const lights = deviceList.filter((d) => /^(SHSW|SHDM|SHRGBW|SNDM|SPDM|S3DM)/.test(d.type || ''));
		const motionSensors = deviceList.filter((d) => isMotion(d.type || ''));
		const relays = deviceList.filter((d) => /^(SHSW|SNSW|SPSW)/.test(d.type || ''));

		// Suggestion 1: Device only - "Turn on [Device]"
		const firstDevice = lights[0] || relays[0] || deviceList[0];
		if (firstDevice) {
			suggestions.push(
				get(format)('ai_suggestion_turn_on_device', {
					values: { deviceName: firstDevice.name },
					default: `Turn on #${firstDevice.name}`,
				}),
			);
		}

		// Suggestion 2: Device + Room - "When [Device] detects motion turn on all lights in [Room]"
		const motionDevice = motionSensors[0] || deviceList[0];
		const firstRoom = roomList[0];
		if (motionDevice && firstRoom) {
			suggestions.push(
				get(format)('ai_suggestion_motion_lights_room', {
					values: { deviceName: motionDevice.name, roomName: firstRoom.name },
					default: `When #${motionDevice.name} detects motion turn on all lights in @${firstRoom.name}`,
				}),
			);
		}

		// Suggestion 3: Group with fallback - "Turn on [Group] at 20:00"
		const firstGroup = groupList[0];
		if (firstGroup) {
			suggestions.push(
				get(format)('ai_suggestion_turn_on_group_time', {
					values: { groupName: firstGroup.name },
					default: `Turn on $${firstGroup.name} at 20:00`,
				}),
			);
		} else {
			suggestions.push(
				get(format)('ai_suggestion_turn_on_garden_lights', {
					default: 'Turn on Garden lights at 20:00',
				}),
			);
		}

		// Fallback to generic if no devices found for first two suggestions
		if (suggestions.length === 0) {
			suggestions.push('Turn on the living room lights');
			suggestions.push('When motion detected, turn on hallway light');
			suggestions.push('Turn on Garden lights at 20:00');
		}

		return suggestions.slice(0, 3);
	}

	function isComplexResponse(content: string): boolean {
		const hasMultipleSteps = (content.match(/\d+\./g) || []).length >= 2;
		const isLong = content.length > 200;
		const hasTechnicalContent = /\b(device|sensor|relay|scene|trigger|action)\b/i.test(content);
		return (isLong && hasTechnicalContent) || hasMultipleSteps;
	}

	// Public function to insert a mention from external components
	export function insertMention(name: string, type: 'room' | 'device' | 'group' | 'scene' = 'device') {
		const symbolMap: Record<string, string> = { room: '@', device: '#', group: '$', scene: '%' };
		const symbol = symbolMap[type] ?? '';
		const tag = `${symbol}${name}`;
		const trimmedInput = userInput.trim();
		const newValue = trimmedInput ? `${trimmedInput} ${tag} ` : `${tag} `;
		userInput = newValue;
		mentionInputComponent?.setValue(newValue);
		setTimeout(() => mentionInputComponent?.focus(), 0);
	}

	// Public function to set the input to a specific prompt (used by dev test prompts)
	export function insertPrompt(prompt: string) {
		userInput = prompt;
		mentionInputComponent?.setValue(prompt);
		setTimeout(() => mentionInputComponent?.focus(), 0);
	}

	// Auto-scroll only when new messages are added.
	// Uses a store subscription instead of a $: block to avoid Svelte's reactive
	// dependency loop (reading + writing prevMessageCount in a $: block causes a freeze).
	let prevMessageCount = 0;

	onMount(() => {
		const unsubscribe = aiSceneChatStore.subscribe((state) => {
			const count = state.messages.length;
			if (count > prevMessageCount && messagesContainer) {
				tick().then(() => {
					if (messagesContainer) {
						messagesContainer.scrollTop = messagesContainer.scrollHeight;
					}
				});
			}
			prevMessageCount = count;
		});
		return unsubscribe;
	});

	// Focus input when modal is ready and we're not showing the onboarding popover (event-driven: no timeout)
	let prevPopoversAllowed = false;
	let prevShowOnboardingPopover = true;
	$: if (popoversAllowed && !showOnboardingPopover && mentionInputComponent) {
		const justBecameAllowed = !prevPopoversAllowed && popoversAllowed;
		const justFinishedTour = prevShowOnboardingPopover && !showOnboardingPopover;
		if (justBecameAllowed || justFinishedTour) {
			tick().then(() => {
				const state = get(aiSceneChatStore);
				const allowed = state.chatModalOpenTransitionDone;
				const showing = state.hasAcceptedOnboarding && !state.chatPopoversCompleted;
				if (allowed && !showing && mentionInputComponent) mentionInputComponent.focus();
			});
		}
		prevPopoversAllowed = popoversAllowed;
		prevShowOnboardingPopover = showOnboardingPopover;
	} else {
		prevPopoversAllowed = popoversAllowed;
		prevShowOnboardingPopover = showOnboardingPopover;
	}

	async function handleSend() {
		const trimmedInput = userInput.trim();
		if (!trimmedInput || $aiSceneChatStore.isLoading) return;

		// Clear input immediately for better UX
		userInput = '';
		historyIndex = -1;
		savedDraft = '';

		// Add user message
		const userMessage: ChatMessageType = {
			role: 'user',
			content: trimmedInput,
			timestamp: Date.now(),
		};
		aiSceneChatStore.addMessage(userMessage);

		// Set loading state and track prompt usage
		aiSceneChatStore.setLoading(true);
		aiSceneChatStore.setError(null);
		aiSceneChatStore.incrementPromptCount($aiSceneChatStore.activeAgentId);

		try {
			// Get conversation history for context
			const conversationHistory = aiSceneChatStore.getConversationHistory();

			// Check if this is the first message after a profile switch (forces backend cache refresh)
			const newSession = aiSceneChatStore.consumeNewSessionFlag();

			// Strip UI token prefixes (#@$%) so the backend AI agent sees clean names
			const apiPrompt = trimmedInput.replace(/[#@$%](?=\S)/g, '');

			const activeAgent = $aiSceneChatStore.activeAgentId;
			const response = await sendAgentMessage(activeAgent, apiPrompt, conversationHistory, {
				newSession,
			});

			const safeContent = response.explanation.replace(/OPTIONS_JSON:\s*\[[\s\S]*?\]/g, '').trim();

			// Determine scenes to upload: use scenesCreated array if available, otherwise single scene
			const scenesToUpload = getScenesForUpload(response.scenesCreated, response.scene);
			const aiMessage: ChatMessageType = {
				role: 'assistant',
				content: safeContent,
				timestamp: Date.now(),
				usage: response.usage,
				sceneCreated: scenesToUpload.length > 0,
				sceneIds: [],
				sceneJson: response.scene,
				devicesUsed: response.devicesUsed,
				intent: response.intent,
				processingTime: response.metadata?.processingTime,
				quickActions: response.options,
				needsExplanation: isComplexResponse(response.explanation),
			};
			aiSceneChatStore.addMessage(aiMessage);
			getAiPromptCount();
			// Auto-upload all created scenes (scene-ai only)
			for (const sceneJson of scenesToUpload) {
				await handleSceneUpload(sceneJson);
			}
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'An error occurred';
			aiSceneChatStore.setError(errorMessage);

			// Add error message to chat
			const errorChatMessage: ChatMessageType = {
				role: 'assistant',
				content: `Sorry, I encountered an error: ${errorMessage}`,
				timestamp: Date.now(),
				isError: true,
			};
			aiSceneChatStore.addMessage(errorChatMessage);
		} finally {
			aiSceneChatStore.setLoading(false);
			aiSceneChatStore.syncChatPreferences();
		}
	}

	async function handleSceneUpload(sceneJson: any) {
		try {
			// Inject AI-generated flag programmatically
			sceneJson._meta = sceneJson._meta || {};
			sceneJson._meta._ai_generated = true;

			// Use app's existing scene/add endpoint directly
			const res = await postReq('/scene/add', {
				scene_script: JSON.stringify(sceneJson),
			});

			if (!res.isok) {
				const errors = res.errors as { message?: string } | undefined;
				throw new Error(errors?.message || 'Failed to save scene');
			}

			const sceneId = res.data?.scene_id;

			// Add scene to local store at the top of the list
			if (sceneId) {
				sceneJson._id = sceneId;
				const allScenes = get(scenes);
				const positions = Object.values(allScenes).map((s: any) => s?._meta?.position ?? 0);
				const minPosition = positions.length > 0 ? Math.min(...positions, 0) : 0;
				sceneJson._meta = { ...sceneJson._meta, position: minPosition - 1 };
				scenes.addScene(sceneJson);

				// Update the last AI message with the scene ID
				const state = get(aiSceneChatStore);
				const lastAiMessage = [...state.messages].reverse().find((m) => m.role === 'assistant');

				if (lastAiMessage) {
					const nextIds = appendSceneId(lastAiMessage.sceneIds, sceneId);
					aiSceneChatStore.updateMessage(lastAiMessage.timestamp, {
						sceneId: nextIds[0],
						sceneIds: nextIds,
					});
				}
			}
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Save failed';

			const uploadErrorMessage: ChatMessageType = {
				role: 'assistant',
				content: `Scene was created but save failed: ${errorMessage}. You can try saving it manually later.`,
				timestamp: Date.now(),
			};
			aiSceneChatStore.addMessage(uploadErrorMessage);
		}
	}

	function handleRetry() {
		const messages = $aiSceneChatStore.messages;
		const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
		if (!lastUserMsg) return;

		userInput = lastUserMsg.content;
		aiSceneChatStore.removeLastMessages(2);
		handleSend();
	}

	function handleQuickAction(message: string) {
		userInput = message;
		handleSend();
	}

	function handleExplainMore() {
		userInput = 'Explain the previous answer in more detail. Do not create or modify any scene.';
		handleSend();
	}
</script>

<!-- Inject global token styles -->
<TokenStyles />

<div class="chat-window">
	<!-- Messages container -->
	<div class="messages-container" bind:this={messagesContainer} role="log" aria-live="polite">
		{#if $aiSceneChatStore.messages.length === 0}
			{#if isHealthCheckLoading}
				<div class="health-check-empty-state">
					<Loader size="l" />
					<Text styleType="body" class="health-check-loading-text">
						{$format('ai_checking_availability', { default: 'Checking availability...' })}
					</Text>
				</div>
			{:else}
				<div class="suggestions-anchor" bind:this={suggestionsAnchorRef}>
					<ChatEmptyState
						{dynamicSceneSuggestions}
						{isMobileDevice}
						{showExamplesOnMobile}
						showExamples={!isInputFocused}
						{renderSuggestionWithTokens}
						on:select={(e) => (userInput = e.detail)}
					>
						<svelte:fragment slot="popover-anchor">
							<AIChatInModalPopover
								step={1}
								show={showOnboardingPopover &&
									popoversAllowed &&
									currentPopoverStep === 1}
								on:next={handlePopoverNext}
								on:skip={handlePopoverSkip}
							>
								<span class="step1-anchor-over-chip"></span>
							</AIChatInModalPopover>
						</svelte:fragment>
					</ChatEmptyState>
				</div>
			{/if}
		{:else}
			{#each $aiSceneChatStore.messages as message (message.timestamp)}
				<ChatMessage
					{message}
					onRetry={getRetryHandler(message)}
					onQuickAction={message.quickActions?.length ? handleQuickAction : undefined}
					onExplainMore={message.needsExplanation ? handleExplainMore : undefined}
				/>
			{/each}
		{/if}

		<!-- Loading indicator -->
		{#if $aiSceneChatStore.isLoading}
			<div class="loading-indicator">
				<div class="loading-animation-mini">
					<FlowAnimation />
				</div>
			</div>
		{/if}

		<!-- Error display -->
		{#if $aiSceneChatStore.error}
			<div class="error-container">
				<Alert type="danger">
					{$aiSceneChatStore.error}
				</Alert>
			</div>
		{/if}
	</div>

	<!-- Bottom section: suggestions + input OR prompt limit notice -->
	<div class="bottom-section">
		{#if promptsExhausted}
			<div class="prompt-limit-banner" role="alert">
				<div class="prompt-limit-content">
					<Icon icon="fas fa-comment-slash" size="s" class="prompt-limit-icon-inline" />
					<div class="prompt-limit-text-group">
						<Text styleType="controls-small" class="prompt-limit-title">
							{$format('ai_prompts_exhausted_title', { default: 'Daily Limit Reached' })}
						</Text>
						{#if isPremium}
							<Text styleType="controls-small" class="prompt-limit-text">
								{$format('ai_prompts_exhausted_premium', {
									default:
										"You've used all {limit} prompts for today. Prompts reset at midnight.",
									values: { limit: promptLimit },
								})}
							</Text>
						{:else}
							<Text styleType="controls-small" class="prompt-limit-text">
								{$format('ai_prompts_exhausted_free', {
									default:
										"You've used all {limit} free prompts. Upgrade for {premiumLimit}/day.",
									values: {
										limit: promptLimit,
										premiumLimit: AI_PROMPT_LIMIT_PREMIUM,
									},
								})}
							</Text>
						{/if}
					</div>
					{#if !isPremium}
						<Button
							variant="premium"
							size="s"
							class="text-capitalize get-premium-button"
							on:click={() => {
								aiSceneChatStore.closeModal();
								push('/settings/premium');
							}}
						>
							{$format('button_get_premium', { default: 'Get Premium' })}
						</Button>
					{/if}
				</div>
			</div>
		{:else}
			{#if mentionActive || activeTokenFilter}
				<ChatSuggestions
					{suggestedDeviceIds}
					{mentionActive}
					filteredItems={mentionFilteredOptions}
					tokenFilter={activeTokenFilter}
					on:mention={handleSuggestionClick}
					on:close={handleSuggestionsClose}
				/>
			{/if}

			<div class="input-area" bind:this={inputAreaRef}>
				<div class="input-wrapper">
					<RichMentionInput
						bind:this={mentionInputComponent}
						bind:value={userInput}
						disabled={isInputDisabled}
						sendDisabled={!userInput.trim() || isInputDisabled}
						{browsingHistory}
						externalAutocomplete={true}
						placeholder={isInputFocused
							? $format('placeholder_ai_scene_chatbot_focused', {
									default: 'Use @ to reference rooms, $ for groups or % for scenes',
								})
							: $format('placeholder_ai_scene_chatbot', {
									default: 'Type your message',
								})}
						on:enter={handleSend}
						on:historyUp={handleHistoryUp}
						on:historyDown={handleHistoryDown}
						on:focus={handleInputFocus}
						on:blur={handleInputBlur}
						on:mentionchange={handleMentionChange}
					>
						<svelte:fragment slot="toolbar-left">
							<AIChatInModalPopover
								step={2}
								show={showOnboardingPopover &&
									popoversAllowed &&
									currentPopoverStep === 2}
								on:next={handlePopoverNext}
								on:skip={handlePopoverSkip}
							>
								<PlusMenuButton
									disabled={isInputDisabled}
									active={activeTokenFilter !== null}
									on:select={handleTokenTypeSelect}
									on:close={handleTokenFilterClear}
									on:refocus={() => mentionInputComponent?.focus()}
								/>
							</AIChatInModalPopover>
						</svelte:fragment>
						<svelte:fragment slot="toolbar-right">
							<AIChatInModalPopover
								step={3}
								show={showOnboardingPopover &&
									popoversAllowed &&
									currentPopoverStep === 3}
								on:next={handlePopoverNext}
								on:skip={handlePopoverSkip}
							>
								<AgentSelector
									disabled={isInputDisabled}
									on:refocus={() => mentionInputComponent?.focus()}
								/>
							</AIChatInModalPopover>
							<AIChatInModalPopover
								step={0}
								show={showOnboardingPopover &&
									popoversAllowed &&
									currentPopoverStep === 0}
								on:next={handlePopoverNext}
								on:skip={handlePopoverSkip}
							>
								<PromptUsageBadge />
							</AIChatInModalPopover>
						</svelte:fragment>
					</RichMentionInput>
				</div>
			</div>
		{/if}
	</div>
</div>

<style>
	.chat-window {
		display: grid;
		grid-template-rows: minmax(0, 1fr) auto;
		height: 100%;
		min-height: 0;
		position: relative;
		overflow: hidden;
	}

	.messages-container {
		min-height: 0;
		overflow-y: auto;
		padding: 16px 8px 16px 2px;
		scroll-behavior: smooth;
		position: relative;
		z-index: 1;
		touch-action: pan-y;
		overscroll-behavior: contain;
	}

	.bottom-section {
		display: flex;
		flex-direction: column;
		position: sticky;
		bottom: 0;
		z-index: 4;
		background: linear-gradient(
			to top,
			rgba(8, 11, 18, 0.94) 0%,
			rgba(8, 11, 18, 0.9) 45%,
			rgba(8, 11, 18, 0) 100%
		);
		-webkit-backdrop-filter: blur(6px);
		backdrop-filter: blur(6px);
	}

	.loading-indicator {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 12px 16px;
		margin: 8px;
		background: transparent;
		border-radius: 12px;
		color: var(--color-text-secondary, #888);
	}

	.loading-animation-mini {
		width: 32px;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.loading-animation-mini :global(canvas) {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.health-check-empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 16px;
		height: 100%;
		padding: 32px;
	}

	.health-check-empty-state :global(.health-check-loading-text) {
		color: var(--color-text-secondary, #a1a2a2);
		text-align: center;
	}

	.error-container {
		margin: 8px;
	}

	.input-area {
		display: flex;
		gap: 12px;
		padding: 2px var(--spacing-m, 16px) var(--spacing-l, 24px);
		background: transparent;
		flex-shrink: 0;
	}

	@media (min-width: 769px) {
		.input-area {
			padding: 2px var(--spacing-l, 24px) var(--spacing-l, 24px);
		}
	}

	.input-wrapper {
		flex: 1;
	}

	.suggestions-anchor {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 100%;
	}

	.step1-anchor-over-chip {
		position: absolute;
		inset: 0;
	}

	.input-area {
		position: relative;
	}

	/* Responsive adjustments */
	@media (max-width: 768px) {
		.input-area {
			gap: 8px;
			padding: 2px var(--spacing-m, 16px) var(--spacing-l, 24px);
		}
	}

	/* Prompt limit banner (replaces the input area) */
	.prompt-limit-banner {
		padding: 16px var(--spacing-m, 16px) var(--spacing-l, 24px);
	}

	@media (min-width: 769px) {
		.prompt-limit-banner {
			padding: 16px var(--spacing-l, 24px) var(--spacing-l, 24px);
		}
	}

	.prompt-limit-content {
		display: flex;
		align-items: center;
		gap: 14px;
		padding: 14px 16px;
		border-radius: 12px;
		background: var(--color-background-3, #1a1a1a);
		border: 1px solid var(--color-border, #2a2a2a);
	}

	.prompt-limit-content :global(.prompt-limit-icon-inline) {
		color: var(--color-text-secondary, #888);
		flex-shrink: 0;
	}

	.prompt-limit-text-group {
		display: flex;
		flex-direction: column;
		gap: 2px;
		flex: 1;
		min-width: 0;
	}

	.prompt-limit-text-group :global(.prompt-limit-title) {
		font-weight: 600;
		color: var(--color-text-primary, #fff);
	}

	.prompt-limit-text-group :global(.prompt-limit-text) {
		color: var(--color-text-secondary, #a1a2a2);
	}

	.prompt-limit-content :global(.get-premium-button) {
		flex-shrink: 0;
		white-space: nowrap;
	}

	@media (max-width: 480px) {
		.prompt-limit-content {
			flex-wrap: wrap;
		}

		.prompt-limit-content :global(.get-premium-button) {
			width: 100%;
			justify-content: center;
		}
	}
</style>
